const { PGAdapter } = require('../common')
const { PaystackInterface } = require('../payment/data-access')
const { getPaymentCustomerByLocationIdQuery, getUserEmailsByLocationIdQuery } = require('./queries')
const { getInsertProperties, getListArgs } = require('./../tools/sql-tools')
const groupBy = require('lodash/groupBy')
const get = require('lodash/get')

/**
 * @typedef {'ng' | 'ke'} PaystackCountries
*/

/**
 * @type {Object<string, PaystackCountries>}
*/
const PaystackCountries = {
  NIGERIA: 'ng',
  KENYA: 'ke'
}

class VirtualAccountsAdapter extends PGAdapter {
  constructor (pgConnection, username, logger) {
    const columns = [
      'id',
      'account_name',
      'account_number',
      'account_id',
      'bank_name',
      'payment_customer_id',
      'currency',
      'is_active',
      'is_assigned',
      'created_at',
      'updated_at'
    ]
    super(pgConnection, 'avocado.virtual_accounts', username, columns, undefined, undefined, logger)
  }

  /**
   * @returns {Promise<any[]>}
  */
  async create (entries) {
    const {columns, placeholders, values} = getInsertProperties(entries)
    const { rows } = await this.pgConnection.query(`
      insert into avocado.virtual_accounts (${columns})
      values ${placeholders}
      on conflict (bank_name, account_number) do update
      set
        account_name = excluded.account_name,
        account_number = excluded.account_number,
        account_id = excluded.account_id,
        bank_name = excluded.bank_name,
        payment_customer_id = excluded.payment_customer_id,
        currency = excluded.currency,
        is_active = excluded.is_active,
        is_assigned = excluded.is_assigned,
        updated_at = now()
      returning *
    `, values)
    return rows
  }
}

class PaymentCustomerAdapter extends PGAdapter {
  constructor (pgConnection, username, logger) {
    const paymentCustomerColumns = [
      'id',
      'location_id',
      'payment_provider',
      'external_id',
      'external_reference',
      'created_at',
      'updated_at'
    ]
    super(pgConnection, 'avocado.payment_customers', username, paymentCustomerColumns, undefined, undefined, logger)
  }

  /**
   * @returns {Promise<any[]>}
   */
  async create (entries) {
    const {columns, placeholders, values} = getInsertProperties(entries)
    const { rows } = await this.pgConnection.query(`
      insert into avocado.payment_customers (${columns})
      values ${placeholders}
      on conflict (external_id, payment_provider) do update
      set
        payment_provider = excluded.payment_provider,
        location_id = excluded.location_id,
        external_id = excluded.external_id,
        external_reference = excluded.external_reference,
        updated_at = now()
      returning *
    `, values)
    return rows
  }
}

class VirtualAccountsApi {
  constructor (state, {
    pgConnection,
    paystackSecrets,
    isomorphicFetch,
    agaveAdapter,
    logger
  }) {
    this.PROVIDERS = {
      paystack: {
        name: 'paystack',
        bankProviders: {
          paystackTitan: 'titan-paystack',
          wema: 'wema-bank',
          testBank: 'test-bank'
        },
        countries: PaystackCountries
      }
    }

    this.virtualAccountsAdapter = new VirtualAccountsAdapter(pgConnection, state.user.name, logger)
    this.paymentCustomerAdapter = new PaymentCustomerAdapter(pgConnection, state.user.name, logger)
    this.pgConnection = pgConnection

    if (paystackSecrets) {
      this.defaultBank = paystackSecrets.environment === 'production'
        ? this.PROVIDERS.paystack.bankProviders.paystackTitan
        : this.PROVIDERS.paystack.bankProviders.testBank
      this.paystackInterface = new PaystackInterface(
        paystackSecrets,
        isomorphicFetch,
        logger
      )
    }
    this.agaveAdapter = agaveAdapter
  }

  formatBankText (bankName, accountNumber, accountName) {
    if (bankName && accountNumber && accountName) {
      return `Pay to\nBank Name: ${bankName}\nAccount Number: ${accountNumber}\nAccount Name: ${accountName}`
    }
    return ''
  }

  async normalizeEmailForPaystack (email, locationUuid) {
    if (!email) return null

    const hasMultipleEmails = email.includes(',')
    if (!hasMultipleEmails) return email.trim()

    const { rows } = await this.pgConnection.query(getUserEmailsByLocationIdQuery, [locationUuid])
    const userEmails = rows.map(row => row.email && row.email.toLowerCase()).filter(Boolean)

    const locationEmails = email.split(',').map(e => e.trim())
    const matchingEmail = locationEmails.find(locEmail =>
      userEmails.includes(locEmail.toLowerCase())
    )

    if (matchingEmail) return matchingEmail

    return email
  }

  /**
    * @param {Object} params
    * @param {PaystackCountries} params.country
    * @param {string} params.locationUuid
    * @param {string} params.nameOfPharmacy
    * @param {string} params.email
    * @param {string} params.phoneNumber
    * @returns {Promise<any>}
  */
  async createPaystackVirtualAccount ({
    country,
    locationUuid,
    nameOfPharmacy,
    email,
    phoneNumber
  }) {
    const error = new Error()
    if (country !== 'ng') {
      error.message = 'Virtual accounts currently not available in your country'
      error.status = 400
      throw error
    }

    const parsedPharmacyName = nameOfPharmacy && nameOfPharmacy.replace(/\s+/g, '')
    if (!parsedPharmacyName || parsedPharmacyName.length <= 0) {
      error.message = 'Unable to parse pharmacy name for KYC validation'
      error.status = 400
      throw error
    }

    const normalizedEmail = await this.normalizeEmailForPaystack(email, locationUuid)

    let paymentCustomer
    const { rows } = await this.pgConnection.query(getPaymentCustomerByLocationIdQuery, [locationUuid])
    if (rows.length >= 1) {
      paymentCustomer = rows[0]
    } else {
      const newCustomer = await this.createPaystackCustomer({ firstName: nameOfPharmacy, email: normalizedEmail, locationUuid, country })
      paymentCustomer = newCustomer
    }

    if (!paymentCustomer) {
      error.message = 'Failed to fetch payment customer, please contact support'
      error.status = 500
      throw error
    }

    // We pass an empty string as the accounts last name for the paystack KYC validation
    const { status, data } = await this.paystackInterface[country].createDedicatedVirtualAccount({
      customerId: paymentCustomer.external_id,
      preferredBank: this.defaultBank,
      firstName: nameOfPharmacy,
      lastName: ' ',
      phoneNumber
    })

    if (!status) {
      error.message = `Failed to create virtual account, please contact support`
      error.status = 500
      throw error
    }

    return this.virtualAccountsAdapter.create([
      {
        account_name: data.account_name,
        account_number: data.account_number,
        account_id: data.id,
        bank_name: data.bank.name,
        payment_customer_id: paymentCustomer.id,
        currency: data.currency,
        is_active: data.active,
        is_assigned: data.assigned
      }
    ])
  }

  /**
   * @param {string} locationUuid
   * @returns {Promise<any[]>}
  */
  async getVirtualAccounts (locationUuid) {
    const { rows } = await this.pgConnection.query(`
      select
        va.id,
        pc.payment_provider as provider,
        va.account_name,
        va.account_number,
        va.bank_name,
        va.currency,
        va.is_active,
        va.created_at,
        va.updated_at
      from avocado.payment_customers pc 
      right join avocado.virtual_accounts va on va.payment_customer_id = pc.id
      where pc.location_id = $1 and va.is_active is true
    `, [locationUuid])
    return rows
  }

  /**
    * @param {Object} params
    * @param {PaystackCountries} params.country
    * @param {string} params.firstName
    * @param {string} params.email
    * @param {string} params.locationUuid
    * @returns {Promise<any>}
  */
  async createPaystackCustomer ({ country, firstName, email, locationUuid }) {
    const customerRequest = {
      email,
      first_name: firstName || '',
      metadata: {
        locationId: locationUuid
      }
    }

    const { data, status } = await this.paystackInterface[country].createPaystackCustomer(customerRequest)
    if (!status) {
      return null
    }

    const [row] = await this.paymentCustomerAdapter.create([
      {
        location_id: locationUuid,
        payment_provider: `PAYSTACK_${country}`.toUpperCase(),
        external_id: data.id,
        external_reference: data.customer_code
      }
    ])
    return row
  }

  /**
    * @param {PaystackCountries} country
    * @returns
  */
  async syncPaystackCustomerData (country) {
    // First sync payment customers
    const allCustomers = []
    const { meta, data, status } = await this.paystackInterface[country].listCustomers()
    if (status) {
      allCustomers.push(...data)
    }

    if (meta.pageCount > 1) {
      for (let pageNumber = 2; pageNumber <= meta.pageCount; pageNumber++) {
        const { data, status } = await this.paystackInterface[country].listCustomers(pageNumber)
        if (status) {
          allCustomers.push(...data)
        }
      }
    }

    const emails = allCustomers.map(c => c.email)
    const {args} = getListArgs(emails)
    const { rows } = await this.pgConnection.query(`
      select
        email,
        uuid as location_id
      from avocado.data_location
      where email in (${args})`, emails)
    const customersByEmail = groupBy(allCustomers, 'email')
    const locationsByEmail = groupBy(rows, 'email')

    const {
      duplicateEmails,
      entries,
      mappedEmails
    } = Object.keys(locationsByEmail).reduce((acc, email) => {
      if (locationsByEmail[email].length > 1) {
        acc.duplicateEmails.push(email)
        return acc
      }
      const customer = customersByEmail[email][0]
      acc.mappedEmails.push(email)
      acc.entries.push({
        location_id: locationsByEmail[email][0].location_id,
        payment_provider: `PAYSTACK_${country}`.toUpperCase(),
        external_id: customer.id,
        external_reference: customer.customer_code
      })
      return acc
    }, {
      entries: [],
      mappedEmails: [],
      duplicateEmails: []
    })

    const unMappedCustomers = Object.keys(customersByEmail).filter(c => !mappedEmails.includes(c))
    const paymentCustomers = await this.paymentCustomerAdapter.create(entries)

    // Fetch all virtual accounts and link them to respectie payment customer
    const allVirtualAccounts = []
    const { meta: dvaMeta, data: dvaData, status: dvaStatus } = await this.paystackInterface[country].getDedicatedAccounts()
    if (dvaStatus) {
      allVirtualAccounts.push(...dvaData)
    }

    if (dvaMeta.pageCount > 1) {
      for (let pageNumber = 2; pageNumber <= dvaMeta.pageCount; pageNumber++) {
        const { data, status } = await this.paystackInterface[country].getDedicatedAccounts(null, pageNumber)
        if (status) {
          allVirtualAccounts.push(...data)
        }
      }
    }

    const paymentCustomersById = groupBy(paymentCustomers, 'external_id')
    const dvaEntries = allVirtualAccounts.map(account => {
      const customerId = get(account, 'customer.id')
      return {
        account_name: account.account_name,
        account_number: account.account_number,
        account_id: account.id,
        bank_name: get(account, 'bank.name'),
        currency: account.currency,
        payment_customer_id: paymentCustomersById[customerId] && paymentCustomersById[customerId][0].id,
        is_assigned: account.assigned,
        is_active: account.active
      }
    })
    const dedicatedAccounts = await this.virtualAccountsAdapter.create(dvaEntries)

    return {
      duplicateEmails,
      unMappedCustomers,
      paymentCustomers: paymentCustomers.length,
      dedicatedAccounts: dedicatedAccounts.length
    }
  }

  async getInvoiceMemo ({ locationUuid, country, email, nameOfPharmacy, ownersPhoneNumber }) {
    let virtualAccount
    const response = await this.getVirtualAccounts(locationUuid)
    virtualAccount = response[0]

    if (!response.length && country === 'ng') {
      const [newVirtualAccount] = await this.createPaystackVirtualAccount({
        country,
        locationUuid,
        nameOfPharmacy,
        email,
        phoneNumber: ownersPhoneNumber
      })
      virtualAccount = newVirtualAccount
    }

    if (virtualAccount) {
      return this.formatBankText(virtualAccount.bank_name, virtualAccount.account_number, virtualAccount.account_name)
    }
    return ''
  }
}

module.exports = VirtualAccountsApi
